// hooks/signalr/useTypingIndicators.js
import { useState, useRef, useCallback } from 'react';
import * as signalR from '@microsoft/signalr';
import debounce from 'lodash/debounce';
import { TYPING_CONFIG } from './signalRConfig';

export const useTypingIndicators = (connectionRef) => {
  const [typingUsers, setTypingUsers] = useState({});
  const typingTimeouts = useRef({});

  // ========== DEBOUNCED TYPING HANDLER ==========
  const handleTypingDebounced = useCallback(
    debounce((station, date, fieldId, isTyping) => {
      if (connectionRef.current?.state === signalR.HubConnectionState.Connected) {
        const method = isTyping ? 'StartTyping' : 'StopTyping';
        connectionRef.current.invoke(method, station, date, fieldId)
          .catch(err => console.error(`Error ${method}:`, err));
      }
    }, TYPING_CONFIG.debounceDelay),
    [connectionRef]
  );

  // ========== START TYPING ==========
  const startTyping = useCallback((station, date, fieldId) => {
    // Rensa befintlig timeout
    if (typingTimeouts.current[fieldId]) {
      clearTimeout(typingTimeouts.current[fieldId]);
    }

    // Skicka "startTyping" till servern
    handleTypingDebounced(station, date, fieldId, true);

    // Auto-stop efter 3 sekunder
    typingTimeouts.current[fieldId] = setTimeout(() => {
      stopTyping(station, date, fieldId);
    }, TYPING_CONFIG.autoStopDelay);
  }, [handleTypingDebounced]);

  // ========== STOP TYPING ==========
  const stopTyping = useCallback((station, date, fieldId) => {
    handleTypingDebounced(station, date, fieldId, false);

    if (typingTimeouts.current[fieldId]) {
      clearTimeout(typingTimeouts.current[fieldId]);
      delete typingTimeouts.current[fieldId];
    }
  }, [handleTypingDebounced]);

  // ========== CLEANUP ==========
  const cleanupTyping = useCallback(() => {
    Object.values(typingTimeouts.current).forEach(clearTimeout);
    typingTimeouts.current = {};
  }, []);

  return {
    typingUsers,
    setTypingUsers,
    startTyping,
    stopTyping,
    cleanupTyping
  };
};